import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { Button } from '@interxfi/design-system/components/button';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { PageTabRail } from '../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../components/router/router-tabbed-navigation';
import { OrganizationBreadcrumb } from '../../../../../../../../features/userauth/organization/organization-breadcrumb';
import { useCanActAsOrganizationAdmin } from '../../../../../../../../features/userauth/organization/userauth-organization-permission-helpers';
import { UserauthOrganizationMemberInvitationCreateDrawer } from '../../../../../../../../features/userauth/organization-member-invitation/userauth-organization-member-invitation-create-drawer';
import { UserauthOrganizationMemberInvitationsTable } from '../../../../../../../../features/userauth/organization-member-invitation/userauth-organization-member-invitations-table';
import { getPageHead } from '../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/organization/_organization/$organizationId/_detail/invitation')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Organization,
      label: 'Invitations',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Organization>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `organization-detail-${params.organizationId}-invitations`,
        label: <OrganizationBreadcrumb organizationId={params.organizationId} />,
        to: `/organization/${params.organizationId}/invitation`,
        suffix: (
          <CopyButton
            content={params.organizationId}
            label="Copy Organization ID"
            tooltip={{ copy: params.organizationId, copied: 'Copied Organization ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Organization'),
  component: OrganizationInvitations,
});

function PlatformMembersTableHeader({ filter }: CustomTableHeaderProps) {
  const { organizationId } = Route.useParams();
  const canActAsAdmin = useCanActAsOrganizationAdmin(organizationId);

  return (
    <PageTabRail
      actions={
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {canActAsAdmin ? (
            <RequiresAccess write>
              <UserauthOrganizationMemberInvitationCreateDrawer
                organizationId={organizationId}
                trigger={<Button affixIcon={<PlusIcon aria-hidden />} aria-label="Send an organization invitation" size="small" variant="primary" />}
              />
            </RequiresAccess>
          ) : null}
          {filter}
        </HorizontalStack>
      }
      group={TabbedNavigationGroup.Organization}
    />
  );
}

function OrganizationInvitations() {
  const { organizationId } = Route.useParams();
  const viewCtx = useMemo(
    () =>
      ({
        kind: 'rollup-organization',
        organizationId,
      }) as const,
    [organizationId],
  );

  return <UserauthOrganizationMemberInvitationsTable HeaderActions={PlatformMembersTableHeader} view={viewCtx} />;
}
